import { PIF_CLASSIFICATION } from "../../../data/constants";
import { PostIfAttributeValueDto, PostIfPositionRulesDto } from "../../../types/pifDto";
import { pifEavFieldId, pifFieldId } from "./pifFocus";
import {
  PIF_BLANK_FORM,
  PifEavValues,
  PifFlow,
  PifFormValues,
  PifGap,
  PifHasLicense,
  isBridgedFieldKey,
} from "./types";

const LICENSE_NUMBER_PATTERN = /^([A-Za-z]\d{18}|\d{9})$/;

/**
 * A plain positive decimal - what the API can actually store.
 *
 * hours_per_week and requested_rate are free TextFields, so "" or "35 hrs" reaches
 * sProc_Portal_PostIFAttributeValues_IU, fails its TRY_CONVERT(DECIMAL(18,2)) check, and - because
 * that proc rejects the ENTIRE batch on one bad row - silently blocks every other answer on the
 * form from saving. Catch it here instead. Rejects symbols, letters, negatives and exponent
 * notation; allows "35", "35.5", "0.5" and ".5".
 */
export const isStorableDecimal = (value: string): boolean => {
  const trimmed = String(value ?? "").trim();
  if (trimmed === "" || trimmed === ".") return false;
  if (!/^[0-9.]+$/.test(trimmed)) return false;
  if (trimmed.split(".").length > 2) return false;
  return Number.isFinite(Number(trimmed));
};

export const isValidLicenseNumber = (licNum: string): boolean => LICENSE_NUMBER_PATTERN.test(licNum || "");

/** A community-based DSP is always part-time and needs a service + client assignment. */
export const isCommunityDsp = (rules: PostIfPositionRulesDto | null): boolean =>
  rules?.classification === PIF_CLASSIFICATION.COMMUNITY_DSP;

/** Full-time is only offered for a schedule-based position that isn't community-based. */
export const isFullTimeAllowed = (rules: PostIfPositionRulesDto | null): boolean =>
  !!rules && rules.isScheduleBased && !isCommunityDsp(rules);

/**
 * The rate check. AC7: outside the *hiring* band is an inline error that blocks submit;
 * outside the wider *position* band is a warning only.
 *
 * A null bound means HR_PositionRates has no value for it, so there is nothing to check
 * against — we do not block on a band we don't know.
 */
export interface PifRateVerdict {
  hasRate: boolean;
  /** True when the rate is outside the hiring band and submit must be refused. */
  blocked: boolean;
  /** True when it clears the hiring band but sits outside the position band. */
  warn: boolean;
  message: string;
}

/** A band bound the API actually supplied. Null/undefined means "no value on file". */
export const isBound = (value: number | null | undefined): value is number => typeof value === "number";

export const getPifRateVerdict = (rate: string, rules: PostIfPositionRulesDto | null): PifRateVerdict => {
  const value = parseFloat(rate);
  const hasRate = (rate || "").trim() !== "" && Number.isFinite(value);

  if (!rules) {
    return { hasRate, blocked: false, warn: false, message: "Pick a position to see the pay band." };
  }

  const { hiringMin, hiringMax, positionMin, positionMax } = rules;

  if (!hasRate) {
    const band =
      isBound(hiringMin) && isBound(hiringMax)
        ? `Hiring band: $${hiringMin}–$${hiringMax}.`
        : "No hiring band is set for this position.";
    return { hasRate, blocked: false, warn: false, message: band };
  }

  if (isBound(hiringMin) && value < hiringMin) {
    return {
      hasRate,
      blocked: true,
      warn: false,
      message: `Below the hiring minimum ($${hiringMin}). Change the rate or have HR raise the band this can't be submitted.`,
    };
  }
  if (isBound(hiringMax) && value > hiringMax) {
    return {
      hasRate,
      blocked: true,
      warn: false,
      message: `Above the hiring maximum ($${hiringMax}). Change the rate or have HR raise the band this can't be submitted.`,
    };
  }

  if (isBound(positionMin) && value < positionMin) {
    return { hasRate, blocked: false, warn: true, message: `Below the position minimum ($${positionMin}). HR and the Director will review it.` };
  }
  if (isBound(positionMax) && value > positionMax) {
    return { hasRate, blocked: false, warn: true, message: `Above the position maximum ($${positionMax}). HR and the Director will review it.` };
  }

  return { hasRate, blocked: false, warn: false, message: "Inside the hiring band no extra scrutiny." };
};

/** The dynamic fields that are required and still unanswered. */
export const getPifEavGaps = (catalogue: PostIfAttributeValueDto[], eav: PifEavValues): PifGap[] =>
  (catalogue || [])
    // A bridged key is validated by its own need(...) call below, so counting it here too would
    // report the same missing answer twice in the footer total.
    .filter((a) => !isBridgedFieldKey(a.fieldKey))
    .filter((a) => a.requiredForEntry && !a.isLocked && !String(eav[a.fieldKey] ?? "").trim())
    .map((a) => ({
      label: a.header || a.fieldKey,
      sec: "details" as const,
      field: pifEavFieldId(a.fieldKey),
    }));

export const getPifGaps = (
  f: PifFormValues,
  flow: PifFlow,
  rules: PostIfPositionRulesDto | null,
  emailRequired: boolean,
  catalogue: PostIfAttributeValueDto[] = [],
  eav: PifEavValues = {},
): PifGap[] => {
  const out: PifGap[] = [];
  // `control` is the PifFormValues key of the input that answers the gap, which is not always the
  // key being checked: a reference dropdown is checked by its label (f.dept) but bound to its id
  // (deptId), and that is the control the error has to focus.
  const need = (v: string, label: string, sec: PifGap["sec"], control: keyof PifFormValues) => {
    if (!String(v || "").trim()) out.push({ label, sec, field: pifFieldId(control) });
  };

  if (flow !== "active") {
    need(f.firstName, "First name", "applicant", "firstName");
    need(f.lastName, "Last name", "applicant", "lastName");
    need(f.dob, "Date of birth", "applicant", "dob");
    need(f.addr1, "Street address", "contact", "addr1");
    need(f.city, "City", "contact", "city");
    need(f.zip, "ZIP", "contact", "zip");
    if (!f.home && !f.cell)
      out.push({ label: "At least one phone number", sec: "contact", field: pifFieldId("home") });
    if (emailRequired) need(f.email, "Personal email", "contact", "email");
  }

  // The licence and vehicle questions only apply where the position allows driving. This gate MUST
  // stay in step with PifSectionContact's `mayOperateVehicle &&` render gate: requiring a field the
  // form does not show leaves the draft permanently unsubmittable with no way to answer it.
  if (rules?.mayOperateVehicle) {
    if (!f.hasLicense)
      out.push({ label: "Driver's license yes or no", sec: "contact", field: pifFieldId("hasLicense") });
    if (f.hasLicense === "yes") need(f.licNum, "License number", "contact", "licNum");
  }

  need(f.hireType, "Kind of hire", "position", "hireType");
  need(f.dept, "Department", "position", "deptId");
  need(f.pos, "Position", "position", "posId");
  need(f.reportsTo, "Reports to", "position", "reportsToId");
  need(f.startDate, "Intended start date", "position", "startDate");
  need(f.hours, "Hours per week", "position", "hours");
  if (String(f.hours || "").trim() && !isStorableDecimal(f.hours))
    out.push({ label: "Hours per week must be a number", sec: "position", field: pifFieldId("hours") });

  if (isCommunityDsp(rules)) {
    need(f.service, "Service", "position", "serviceId");
    need(f.client, "Individual / client", "position", "clientId");
  }

  need(f.rateType, "Rate type", "pay", "rateTypeId");
  need(f.rate, "Rate", "pay", "rate");
  if (String(f.rate || "").trim() && !isStorableDecimal(f.rate))
    out.push({ label: "Rate must be a number", sec: "pay", field: pifFieldId("rate") });
  if (getPifRateVerdict(f.rate, rules).blocked)
    out.push({ label: "Rate is outside the hiring band", sec: "pay", field: pifFieldId("rate") });

  out.push(...getPifEavGaps(catalogue, eav));

  return out;
};

/**
 * How many answers this form requires in total — the denominator behind "7/15".
 *
 * Counted by running the gap check over a blank form rather than by hand, so the
 * numerator and denominator can never disagree about what "required" means. (The previous
 * hand-tallied constants were already one short of the fields the gap check asked for.)
 */
export const getPifRequiredTotal = (
  flow: PifFlow,
  rules: PostIfPositionRulesDto | null,
  emailRequired: boolean,
  catalogue: PostIfAttributeValueDto[] = [],
  hasLicense: PifHasLicense = "",
): number => {
  const blank: PifFormValues = { ...PIF_BLANK_FORM, hasLicense };
  return getPifGaps(blank, flow, rules, emailRequired, catalogue, {}).length;
};
